/*
 * Lead capture (snippets/lead-source.liquid).
 *
 * Every form that posts through Shopify's contact form only produces an email
 * to the store. This keeps a record as well: once Shopify has accepted a
 * submission, the same answers go to Klaviyo as a "Submitted Website Form"
 * event on the sender's profile, with the form and the page it came from.
 *
 * Two steps, because the post reloads the page:
 *   1. on submit, the form's answers are put aside in sessionStorage;
 *   2. on the page Shopify returns to (?contact_posted=true), they are sent
 *      and the copy is removed, so a reload of that URL sends nothing twice.
 * A submission Shopify rejects never reaches step 2, so it is not recorded.
 *
 * The event subscribes nobody to anything. A form can carry a tick-box marked
 * `data-nb-consent` ("email me updates"); only when it was ticked is the
 * sender also subscribed, to the list named by the script's
 * `data-optin-list` (Klaviyo's "Website enquiry opt-ins").
 *
 * A form that does not post through Shopify (the green walls page posts to
 * Formspree) calls window.nbLeadCapture.record(answers, optin) itself once its
 * own service has accepted the submission.
 *
 * Each lead also carries where the visitor came from: the Google Ads click id
 * and campaign tags (gclid, utm_...) of the visit that brought them, kept 90
 * days, so the B2B portal can credit the ad and report the booking back to
 * Google Ads. They come from this page's address, or from the `nb_attr` cookie
 * the portal's tracking pixel sets when the visitor landed on another page,
 * plus Shopify's visitor cookie, which links the lead to the pages they read.
 *
 * The same lead also goes straight to the B2B portal (the script's
 * `data-portal` address), which emails the team at once: Klaviyo's flows take
 * minutes. Both copies carry one `lead_id`, so when the Klaviyo flow's webhook
 * brings the lead to the portal as well, it is not made twice; if this send is
 * lost, the Klaviyo copy still arrives.
 *
 * Only decorates: if this script or Klaviyo fails, the form and its email are
 * untouched.
 */
(function () {
  'use strict';

  // The snippet is rendered once per form, so this file can be asked for
  // several times on one page.
  if (window.nbLeadCapture) return;

  var script = document.currentScript;
  var KEY = script ? script.getAttribute('data-klaviyo') : '';
  var LIST = script ? script.getAttribute('data-optin-list') : '';
  var PORTAL = script ? script.getAttribute('data-portal') : '';
  var API = 'https://a.klaviyo.com/client/';
  var REVISION = '2026-07-15';
  var STASH = 'nb:lead';
  var METRIC = 'Submitted Website Form';
  var MARKER = 'input[name="contact[form]"]';
  var CONSENT = 'input[data-nb-consent]';
  var ATTR = 'nb:attr';
  var TAGS = ['gclid', 'gbraid', 'wbraid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  var KEEP = 90 * 864e5;

  function cookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    if (!match) return '';
    try { return decodeURIComponent(match[1]); } catch (err) { return ''; }
  }

  // An ad click or campaign link on this page's address is kept until a
  // newer one replaces it.
  function remember() {
    var params = new URLSearchParams(location.search);
    var tagged = {};
    var found = false;
    TAGS.forEach(function (key) {
      var value = params.get(key);
      if (value) { tagged[key] = value.slice(0, 200); found = true; }
    });
    if (!found) return;
    tagged.landing_page = location.pathname;
    tagged.referrer = document.referrer || '';
    tagged.click_at = new Date().toISOString();
    try { localStorage.setItem(ATTR, JSON.stringify(tagged)); } catch (err) {}
  }

  // The newer of this script's copy and the pixel's cookie, if under 90 days.
  function attribution() {
    var own = null;
    var pixel = null;
    try { own = JSON.parse(localStorage.getItem(ATTR) || 'null'); } catch (err) {}
    try { pixel = JSON.parse(cookie('nb_attr') || 'null'); } catch (err) {}
    var found = own;
    if (pixel && (!own || String(pixel.click_at || '') > String(own.click_at || ''))) found = pixel;
    if (found && !(Date.now() - Date.parse(found.click_at) < KEEP)) found = null;

    var out = {};
    if (found) {
      TAGS.concat(['landing_page', 'referrer', 'click_at']).forEach(function (key) {
        if (found[key]) out[key] = String(found[key]).slice(0, 300);
      });
    }
    var visitor = cookie('_shopify_y');
    if (visitor) out.shopify_y = visitor;
    return out;
  }

  // Only the contact[...] fields are answers; Shopify's own fields and the
  // captcha's are left behind, and so is anything that is not text (a file).
  function answers(form) {
    var data = {};

    new FormData(form).forEach(function (value, name) {
      var match = /^contact\[(.+)\]$/.exec(name);
      if (!match || typeof value !== 'string') return;

      value = value.trim();
      if (!value) return;

      var key = match[1];
      data[key] = data[key] ? data[key] + ', ' + value : value;
    });

    return data;
  }

  // keepalive: the visitor may leave the page before the answer comes back.
  function post(path, data) {
    return fetch(API + path + '?company_id=' + encodeURIComponent(KEY), {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: REVISION },
      body: JSON.stringify({ data: data }),
      keepalive: true,
    }).catch(function () {});
  }

  function newId() {
    try { if (window.crypto && crypto.randomUUID) return crypto.randomUUID(); } catch (err) {}
    return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
  }

  // Plain text, so no preflight; a beacon survives the visitor leaving the page.
  function toPortal(body) {
    if (!PORTAL) return;
    var data = JSON.stringify(body);
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(PORTAL, new Blob([data], { type: 'text/plain' }))) return;
    } catch (err) {}
    try {
      fetch(PORTAL, { method: 'POST', mode: 'no-cors', keepalive: true, headers: { 'Content-Type': 'text/plain' }, body: data })
        .catch(function () {});
    } catch (err) {}
  }

  function record(lead, optin) {
    if (!lead || !lead.email || !KEY || !window.fetch) return;

    var names = (lead.name || '').trim().split(/\s+/);
    var profile = { email: lead.email };
    if (names[0]) profile.first_name = names[0];
    if (names.length > 1) profile.last_name = names.slice(1).join(' ');
    if (lead.company) profile.organization = lead.company;

    // Every answer is a property of the event. `details` is the same thing as
    // one block of text, for a notification email to print as it stands.
    var properties = {};
    var lines = [];
    Object.keys(lead).forEach(function (key) {
      properties[key] = lead[key];
      lines.push(key + ': ' + lead[key]);
    });
    properties.details = lines.join('\n');

    // Where they came from rides along, outside `details`, which is for people.
    var source = attribution();
    Object.keys(source).forEach(function (key) {
      if (!properties[key]) properties[key] = source[key];
    });

    // One id for both copies (see the top of this file). The portal first: it
    // is the one that alerts the team.
    properties.lead_id = newId();
    toPortal({ lead_id: properties.lead_id, email: lead.email, properties: properties });

    post('events', {
      type: 'event',
      attributes: {
        properties: properties,
        metric: { data: { type: 'metric', attributes: { name: METRIC } } },
        profile: { data: { type: 'profile', attributes: profile } },
      },
    });

    if (!optin || !LIST) return;

    post('subscriptions', {
      type: 'subscription',
      attributes: {
        custom_source: 'Website enquiry: ' + (lead.form || 'form'),
        profile: {
          data: {
            type: 'profile',
            attributes: { email: lead.email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } },
          },
        },
      },
      relationships: { list: { data: { type: 'list', id: LIST } } },
    });
  }

  window.nbLeadCapture = { record: record };
  remember();

  // Capture phase: the copy is taken before any other handler has a chance to
  // stop the event on its way up.
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.querySelector || !form.querySelector(MARKER)) return;

    var consent = form.querySelector(CONSENT);
    var stash = { answers: answers(form), optin: Boolean(consent && consent.checked) };
    try { sessionStorage.setItem(STASH, JSON.stringify(stash)); } catch (err) {}
  }, true);

  if (!/[?&]contact_posted=true(&|$)/.test(location.search)) return;

  var stash;
  try {
    stash = JSON.parse(sessionStorage.getItem(STASH) || 'null');
    sessionStorage.removeItem(STASH);
  } catch (err) {}

  if (!stash) return;
  // A copy put aside by the previous version of this file holds the answers alone.
  if (stash.answers) {
    record(stash.answers, stash.optin);
  } else {
    record(stash, false);
  }
})();
